import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { useUpsertCredential } from "../../hooks/useCredentials";
import type {
  CredentialKind,
  ResolvedCredential,
  UpsertCredentialBody,
} from "../../types";

const KIND_TITLE: Record<CredentialKind, string> = {
  pms: "PMS login",
  website: "Website / CMS login",
};

/** Where the displayed platform comes from, named as the operator would find
 *  it on the hotel's record. */
const PLATFORM_SOURCE: Record<CredentialKind, string> = {
  pms: "PMS",
  website: "website builder",
};

/**
 * Record this hotel's own login for one kind.
 *
 * Every field is pre-filled from what resolves today, the password included:
 * the store holds these in the clear and the card this opens from already
 * shows them, so an empty box would only misreport what is stored. The
 * password is written back ONLY when its text actually changes — an
 * untouched pre-fill is omitted from the body, so fixing a typo'd username
 * neither re-submits the secret nor silently captures a shared default onto
 * this hotel's own row. Clearing remains an explicit checkbox, not something
 * you can do by deleting text.
 *
 * Fields left blank are recorded as blank and fall through to the platform
 * default on the next read, which is the same thing the resolver does with a
 * missing value — so an operator who only knows this hotel's password can
 * fill in that one box and let the rest resolve.
 *
 * The platform is shown, not asked for. It is a fact about the hotel, held
 * once in the hotel's own record and echoed here; a second copy typed in
 * beside the password would drift from it silently, and the drift matters
 * most on the hotel that has migrated off the platform it is still matched
 * on. So this modal never writes `label`.
 */
export function CredentialEditModal({
  userId,
  kind,
  platform,
  current,
  onClose,
}: {
  userId: string;
  kind: CredentialKind;
  /** The hotel's platform, from its own record. Display only. */
  platform: string | null;
  current: ResolvedCredential | null;
  onClose: () => void;
}) {
  // Seeded from what is resolved today, including values that came from the
  // platform default: an operator opening this to record ONE bespoke field
  // should not have to retype the rest to keep it.
  const seededUsername = current?.username?.value ?? "";
  const [username, setUsername] = useState(seededUsername);
  const seededPassword = current?.password?.value ?? "";
  const [password, setPassword] = useState(seededPassword);
  const [clearPassword, setClearPassword] = useState(false);
  const seededLoginUrl = current?.loginUrl?.value ?? "";
  const [loginUrl, setLoginUrl] = useState(seededLoginUrl);
  const seededNote = current?.note?.value ?? "";
  const [note, setNote] = useState(seededNote);

  const upsert = useUpsertCredential(userId);

  // "Stored" means stored AGAINST THIS HOTEL. A password resolving from the
  // platform default is not this hotel's to keep or clear: offering "clear
  // the stored password" for one would write a null onto the override row,
  // the resolver would fall straight back through to the same default, and
  // the operator would watch the password they just cleared reappear.
  const hasOwnSecret = current?.password?.provenance === "hotel-override";
  const inheritsSecret = Boolean(current?.password) && !hasOwnSecret;

  const submit = () => {
    // Same tri-state rule as password, for every field: an untouched
    // pre-fill is omitted, not resubmitted. Otherwise a field seeded from
    // the platform default (not this hotel's own) gets written back
    // unchanged the moment the operator edits a DIFFERENT field, silently
    // turning an inherited value into a durable hotel-specific override.
    const body: UpsertCredentialBody = {};
    if (username.trim() !== seededUsername) body.username = username.trim() || null;
    if (loginUrl.trim() !== seededLoginUrl) body.loginUrl = loginUrl.trim() || null;
    if (note.trim() !== seededNote) body.note = note.trim() || null;
    // Tri-state: omit the key entirely to leave the secret untouched. The
    // pre-fill counts as untouched — only a real edit re-submits a password.
    if (clearPassword) body.password = null;
    else if (password.trim() && password !== seededPassword)
      body.password = password;

    upsert.mutate({ kind, body }, { onSuccess: onClose });
  };

  return (
    <ActionModal
      title={KIND_TITLE[kind]}
      submitLabel="Save"
      onSubmit={submit}
      onClose={onClose}
      pending={upsert.isPending}
    >
      <p className="text-sm text-content-secondary">
        Recorded against this hotel specifically. Anything left blank falls
        back to the platform default.
      </p>

      <div>
        {/* Not a <label>: there is no control here to label. */}
        <div className={FIELD_LABEL_CLASS}>Platform</div>
        <p className="text-sm text-content-primary">
          {platform ?? (
            <span className="text-content-tertiary italic">
              none on file for this hotel
            </span>
          )}
        </p>
        <p className="text-xs text-content-tertiary mt-1">
          From this hotel&apos;s {PLATFORM_SOURCE[kind]} record, which is what
          the shared login is matched on. Change it there, not here.
        </p>
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>Username</label>
        <input
          className={INPUT_CLASS}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="success@autumnplatform.com"
        />
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>
          Password
          {inheritsSecret && (
            <span className="font-normal text-content-tertiary">
              {" "}
              — the shared one; editing it records this hotel&apos;s own copy
            </span>
          )}
        </label>
        <input
          className={INPUT_CLASS}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={clearPassword}
          autoComplete="off"
        />
        {hasOwnSecret && (
          <label className="flex items-center gap-2 mt-2 text-xs text-content-secondary">
            <input
              type="checkbox"
              checked={clearPassword}
              onChange={(e) => setClearPassword(e.target.checked)}
            />
            Clear this hotel&apos;s password
            <span className="text-content-tertiary">
              (the shared one applies again)
            </span>
          </label>
        )}
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>Sign-in URL (optional)</label>
        <input
          className={INPUT_CLASS}
          value={loginUrl}
          onChange={(e) => setLoginUrl(e.target.value)}
          placeholder="https://example.com/wp-admin/"
        />
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>
          Note (optional) — 2FA routing, recovery codes, account role
        </label>
        <textarea
          className={INPUT_CLASS}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="2FA goes to Christina's phone"
        />
      </div>
    </ActionModal>
  );
}
